import { Component, Inject, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { UpdateProductHandlingUnits, UpdateProductHandlingUnitsSuccess } from '../../store/produits.actions';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription, tap, Observable, of, combineLatest, map, startWith } from 'rxjs';
import { IHandlingUnitType } from 'src/app/features/catalogs/models/catalogs.models';
import { CatalogsService } from 'src/app/features/catalogs/services/catalogs.services';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AddElementsComponent } from '../add-elements/add-elements.component';
import { IProductHandlingUnit } from '../../models/products.models';
import { HU_ROUNDING_MODES } from './handling-unit-rounding-modes';

/** Quantity / multiplier must be a finite number > 0 (decimals allowed). */
export function positiveQuantityValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      return { positiveQuantity: true };
    }
    return null;
  };
}

export interface DialogData {
  productVariantId: number;
  /** Pre-fill a single row to edit an existing selling unit (sends `id` on save). */
  editUnit?: IProductHandlingUnit;
  /** All current units for this product — used to block ERP-managed types. */
  existingUnits?: IProductHandlingUnit[];
}

@Component({
  selector: 'app-handling-units-dialogue',
  templateUrl: './handling-units-dialogue.component.html',
  styleUrls: ['./handling-units-dialogue.component.scss']
})
export class HandlingUnitsDialogueComponent implements OnInit, OnDestroy {
  handlingUnitsForm: FormGroup;
  private subscriptions: Subscription[] = [];
  availableHandlingUnitTypes: IHandlingUnitType[] = [];
  filteredHandlingUnitTypes$: { [key: number]: Observable<IHandlingUnitType[]> } = {};
  @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers!: QueryList<MatAutocompleteTrigger>;

  readonly roundingModes = HU_ROUNDING_MODES;
  expandedAdvanced = new Set<number>();
  /** Advanced fields are only sent once the developer panel has been opened for that row */
  advancedEverOpened = new Set<number>();

  get isEditMode(): boolean {
    return !!this.data?.editUnit;
  }

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<HandlingUnitsDialogueComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private store: Store<AppState>,
    private actions$: Actions,
    private catalogService: CatalogsService,
    private dialog: MatDialog
  ) {
    this.handlingUnitsForm = this.fb.group({
      units: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadHandlingUnitTypes();
    this.initializeForm();

    const sub = this.actions$
      .pipe(
        ofType(UpdateProductHandlingUnitsSuccess),
        tap(() => {
          this.dialogRef.close(true);
        })
      )
      .subscribe();

    this.subscriptions.push(sub);
  }

  private loadHandlingUnitTypes(): void {
    this.catalogService.getAvailableHandlingUnits().subscribe({
      next: (response) => {
        this.availableHandlingUnitTypes = Array.isArray(response.data)
          ? response.data
          : (response.data ? [response.data] : []);
        this.initializeFilters();
      },
      error: () => {
        this.availableHandlingUnitTypes = [];
      }
    });
  }

  private initializeFilters(): void {
    this.units.controls.forEach((control, index) => {
      this.setupFilterForControl(index);
    });
  }

  private erpManagedNames(): Set<string> {
    return new Set(
      (this.data.existingUnits ?? [])
        .filter(u => u.erp_managed)
        .map(u => u.name.toLowerCase())
    );
  }

  private erpManagedNameValidator(): ValidatorFn {
    const blocked = this.erpManagedNames();
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = typeof control.value === 'string'
        ? control.value
        : (control.value?.name ?? '');
      return blocked.has(raw.trim().toLowerCase()) ? { erpManagedType: true } : null;
    };
  }

  private setupFilterForControl(index: number): void {
    const nameControl = this.units.at(index).get('name');
    if (nameControl) {
      const blocked = this.erpManagedNames();
      this.filteredHandlingUnitTypes$[index] = combineLatest([
        of(this.availableHandlingUnitTypes),
        nameControl.valueChanges.pipe(
          startWith(nameControl.value || ''),
          map(value => {
            return typeof value === 'string' ? value : (value?.name || '');
          })
        )
      ]).pipe(
        map(([allTypes, search]) => {
          const searchLower = (search || '').toLowerCase();
          return allTypes.filter(type => {
            if (blocked.has(type.name.toLowerCase())) return false;
            return !searchLower || type.name.toLowerCase().includes(searchLower);
          });
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get units(): FormArray {
    return this.handlingUnitsForm.get('units') as FormArray;
  }

  toggleAdvanced(index: number): void {
    if (this.expandedAdvanced.has(index)) {
      this.expandedAdvanced.delete(index);
    } else {
      this.expandedAdvanced.add(index);
      this.advancedEverOpened.add(index);
    }
  }

  isAdvancedOpen(index: number): boolean {
    return this.expandedAdvanced.has(index);
  }

  private initializeForm(): void {
    while (this.units.length) {
      this.units.removeAt(0);
    }
    this.filteredHandlingUnitTypes$ = {};
    this.expandedAdvanced.clear();
    this.advancedEverOpened.clear();

    if (this.data.editUnit) {
      const seed = this.unitToFormSeed(this.data.editUnit);
      this.units.push(this.createUnitFormGroup(seed));
      // Advanced keys are sent only after the user opens the advanced section (toggleAdvanced).
      setTimeout(() => this.setupFilterForControl(0), 0);
    } else {
      this.addUnit();
    }
  }

  /** Odoo JSON often uses false for empty selection / unset numbers. */
  private normalizeRoundingMode(mode: unknown): string {
    if (mode == null || mode === false || mode === '') {
      return 'ROUND_UP';
    }
    return String(mode);
  }

  private normalizeOptionalNumber(n: unknown): number | null {
    if (n == null || n === false || n === '') {
      return null;
    }
    const v = Number(n);
    return Number.isFinite(v) ? v : null;
  }

  /** Positive multiplier / legacy quantity; Odoo JSON may use false for unset. */
  private asOptionalPositiveNumber(val: unknown): number | null {
    if (val == null || val === false || val === '') {
      return null;
    }
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) {
      return null;
    }
    return n;
  }

  private asRoundingDecimals(val: unknown): number {
    if (val == null || val === false || val === '') {
      return 0;
    }
    const n = Number(val);
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.max(0, Math.round(n));
  }

  private unitToFormSeed(hu: IProductHandlingUnit): Record<string, unknown> {
    const mult = this.asOptionalPositiveNumber(hu.multiplier_from_base_unit as unknown);
    const qLegacy = this.asOptionalPositiveNumber(hu.quantity as unknown) ?? 1;
    const displayQty = mult ?? qLegacy;

    const multStr = mult != null && mult !== qLegacy ? String(mult) : '';

    const roundingDecimals = this.asRoundingDecimals(hu.rounding_decimals as unknown);

    const qMin = this.normalizeOptionalNumber(hu.qty_min);
    const qMax = this.normalizeOptionalNumber(hu.qty_max);
    const qInc = this.normalizeOptionalNumber(hu.qty_increment);

    return {
      id: hu.id,
      name: hu.name ?? '',
      quantity: displayQty,
      dimensions: (hu.dimenssion_string && String(hu.dimenssion_string)) || '',
      rounding_mode: this.normalizeRoundingMode(hu.rounding_mode),
      rounding_decimals: roundingDecimals,
      qty_min: qMin != null ? String(qMin) : '',
      qty_max: qMax != null ? String(qMax) : '',
      qty_increment: qInc != null ? String(qInc) : '',
      is_continuous: !!hu.is_continuous,
      multiplier_from_base_unit: multStr,
    };
  }

  private createUnitFormGroup(unit?: Record<string, unknown>): FormGroup {
    return this.fb.group({
      id: [unit?.['id'] ?? null],
      name: [unit?.['name'] ?? '', [Validators.required, this.erpManagedNameValidator()]],
      quantity: [unit?.['quantity'] ?? 1, [Validators.required, positiveQuantityValidator()]],
      dimensions: [unit?.['dimensions'] ?? ''],
      rounding_mode: [unit?.['rounding_mode'] ?? 'ROUND_UP'],
      rounding_decimals: [unit?.['rounding_decimals'] ?? 0, [Validators.min(0)]],
      qty_min: [unit?.['qty_min'] ?? ''],
      qty_max: [unit?.['qty_max'] ?? ''],
      qty_increment: [unit?.['qty_increment'] ?? ''],
      is_continuous: [unit?.['is_continuous'] ?? false],
      multiplier_from_base_unit: [unit?.['multiplier_from_base_unit'] ?? ''],
    });
  }

  addUnit(defaultValues?: Record<string, unknown>): void {
    const newIndex = this.units.length;
    this.units.push(this.createUnitFormGroup(defaultValues));
    setTimeout(() => {
      this.setupFilterForControl(newIndex);
    }, 0);
  }

  removeUnit(index: number): void {
    if (this.units.length > 1) {
      this.units.removeAt(index);
      delete this.filteredHandlingUnitTypes$[index];
      const newFilters: { [key: number]: Observable<IHandlingUnitType[]> } = {};
      Object.keys(this.filteredHandlingUnitTypes$).forEach(key => {
        const oldIndex = parseInt(key, 10);
        if (oldIndex < index) {
          newFilters[oldIndex] = this.filteredHandlingUnitTypes$[oldIndex];
        } else if (oldIndex > index) {
          newFilters[oldIndex - 1] = this.filteredHandlingUnitTypes$[oldIndex];
        }
      });
      this.filteredHandlingUnitTypes$ = newFilters;

      const nextExpanded = new Set<number>();
      this.expandedAdvanced.forEach(j => {
        if (j === index) return;
        nextExpanded.add(j > index ? j - 1 : j);
      });
      this.expandedAdvanced = nextExpanded;

      const nextAdvOpen = new Set<number>();
      this.advancedEverOpened.forEach(j => {
        if (j === index) return;
        nextAdvOpen.add(j > index ? j - 1 : j);
      });
      this.advancedEverOpened = nextAdvOpen;
    }
  }

  onHandlingUnitTypeSelected(type: IHandlingUnitType, index: number): void {
    const nameControl = this.units.at(index).get('name');
    if (nameControl && type && type.name) {
      nameControl.setValue(type.name, { emitEvent: false });
    }
  }

  addNewHandlingUnitType(newElementName: string, index: number): void {
    const dialogRef = this.dialog.open(AddElementsComponent, {
      data: {
        name: newElementName,
        type: 'handling_unit_type',
        index: index
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.autocompleteTriggers.forEach(trigger => trigger.closePanel());
      this.loadHandlingUnitTypes();
    });
  }

  getFilteredHandlingUnitTypes(index: number): Observable<IHandlingUnitType[]> {
    return this.filteredHandlingUnitTypes$[index] || of([]);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submitPermission() {
    return { module: 'product', action: this.isEditMode ? 'edit' : 'create' };
  }

  /** ngx-translate key for inline rounding help (summary / example). */
  roundingModeHelpKey(mode: string | null | undefined, part: 'summary' | 'example'): string {
    if (!mode) {
      return '';
    }
    return `inventory.inventory_tab.handling_unit.rounding_help.modes.${mode}.${part}`;
  }

  private optionalPositiveNumber(raw: unknown): number | undefined {
    if (raw === '' || raw === null || raw === undefined) {
      return undefined;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      return undefined;
    }
    return n;
  }

  private optionalNumber(raw: unknown): number | undefined {
    if (raw === '' || raw === null || raw === undefined) {
      return undefined;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      return undefined;
    }
    return n;
  }

  save(): void {
    if (this.handlingUnitsForm.valid) {
      const formValues = this.units.getRawValue() as Record<string, unknown>[];

      const handlingUnits = formValues.map((unit, index) => {
        const q = Number(unit['quantity']);
        const payload: Record<string, unknown> = {
          name: String(unit['name'] ?? '').trim(),
          unit_quantity: q,
          // Manually added selling units can never be ERP allowed — the ERP is the
          // source of truth for that flag (WB-6368). Always force it to false so the
          // invariant holds on the edit path too, where advanced is never expanded and
          // mergeExisting would otherwise preserve a legacy `true`.
          is_allowed_to_erp: false,
        };

        const dim = String(unit['dimensions'] ?? '').trim();
        if (dim) {
          payload['dimenssion_string'] = dim;
        }

        const hid = unit['id'];
        const hidNum = Number(hid);
        if (hid != null && hid !== '' && Number.isFinite(hidNum) && hidNum > 0) {
          payload['id'] = hidNum;
        }

        if (this.advancedEverOpened.has(index)) {
          payload['rounding_mode'] = unit['rounding_mode'] || 'ROUND_UP';
          payload['rounding_decimals'] = Math.max(0, Math.round(Number(unit['rounding_decimals'] ?? 0)));
          payload['is_continuous'] = !!unit['is_continuous'];

          const mult = this.optionalPositiveNumber(unit['multiplier_from_base_unit']);
          if (mult !== undefined && mult > 0) {
            payload['multiplier_from_base_unit'] = mult;
          }

          for (const key of ['qty_min', 'qty_max', 'qty_increment'] as const) {
            const v = this.optionalNumber(unit[key]);
            if (v !== undefined) {
              payload[key] = v;
            }
          }
        }

        return payload;
      });

      this.store.dispatch(UpdateProductHandlingUnits({
        productVariantId: this.data.productVariantId,
        handlingUnits,
        mergeExisting: true,
      }));

    } else {
      this.units.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }
}
